import Modal from '@/app/_components/modal';
import { Sheet } from '@/app/_components/Sheet';
import { ClinicHeader } from '@/app/_components/widgets/listing/card/_components/ClinicHeader';
import { PriceSection } from '@/app/_components/widgets/listing/card/_components/PriceSection';
import { useIsDesktop } from '@/app/_hooks/useIsScreen';
import { SpecialityByIdDocument } from '@/types-aggregatore';
import { useQuery } from '@apollo/client';
import { ELButton } from '@davincihealthcare/elty-design-system-react';
import { QuestionMarkCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { EltyInstructionCard, WorkgroupInstructionCard } from '@/app/_components/widgets/checkoutWidget/recap/InstructionCards';
import { monaFont } from '@/styles/fonts';

type SpecialityProps = {
  specialityId: string | null;
  specialityName: string;
  price: number | undefined;
  hasDynamicPrice: boolean;
  hasDescription: boolean;
};

type WorkgroupProps = {
  workgroupId: string;
  logoUrl: string | undefined;
  clinicName: string;
  clinicLink: string;
  completeAddress: string;
  isLicensed: boolean;
  isDomicile: boolean;
};

const SpecialityWrapper = ({ onClick, children }: { onClick: (() => void) | undefined; children: React.ReactNode }) => {
  if (!onClick) {
    return <>{children}</>;
  }

  return (
    <button className="flex flex-col gap-1 overflow-hidden text-left" onClick={onClick}>
      {children}
    </button>
  );
};

export const Speciality = ({
  specialityName,
  specialityId,
  workgroupId,
  logoUrl,
  clinicName,
  clinicLink,
  completeAddress,
  isLicensed,
  isDomicile,
  price,
  hasDynamicPrice,
  hasDescription,
  onChooseDate,
  disabled,
}: SpecialityProps & WorkgroupProps & { onChooseDate: () => void; disabled?: boolean }) => {
  const [isSpecialityDescriptionModalOpen, setIsSpecialityDescriptionModalOpen] = useState(false);

  return (
    <>
      {isSpecialityDescriptionModalOpen && specialityId && (
        <SpecializationDescriptionModal
          onClose={() => setIsSpecialityDescriptionModalOpen(false)}
          specialityId={specialityId}
          workgroupId={workgroupId}
          logoUrl={logoUrl}
          clinicName={clinicName}
          clinicLink={clinicLink}
          completeAddress={completeAddress}
          isLicensed={isLicensed}
          isDomicile={isDomicile}
          price={price}
          hasDynamicPrice={hasDynamicPrice}
          onChooseDate={onChooseDate}
        />
      )}

      <SpecialityWrapper onClick={hasDescription && specialityId ? () => setIsSpecialityDescriptionModalOpen(true) : undefined}>
        <h3
          className={`${monaFont.className} m-0 break-words text-[22px] font-bold leading-[28px] tracking-normal ${disabled ? 'text-neutral-inactive' : 'text-primary'} ${hasDescription ? 'cursor-pointer' : ''}`}
        >
          <span className="inline">
            {specialityName}
            {hasDescription && (
              <QuestionMarkCircleIcon data-cy="speciality-info-icon" className="mb-0.5 ml-1 inline size-5 flex-shrink-0 text-primary" />
            )}
          </span>
        </h3>
      </SpecialityWrapper>
    </>
  );
};

const SpecializationDescriptionModal = ({
  specialityId,
  workgroupId,
  logoUrl,
  clinicName,
  clinicLink,
  completeAddress,
  isLicensed,
  onClose,
  price,
  onChooseDate,
  hasDynamicPrice,
  isDomicile,
}: WorkgroupProps & { onClose: () => void; onChooseDate: () => void; specialityId: string } & Pick<
    SpecialityProps,
    'price' | 'hasDynamicPrice'
  >) => {
  const { data } = useQuery(SpecialityByIdDocument, {
    variables: {
      id: specialityId,
    },
  });

  const onChooseDateAndClose = () => {
    onChooseDate();
    onClose();
  };

  const description = data?.specialityById?.eltyDescription;
  const eltyInstructions = data?.specialityById?.eltyInstructions;
  const messagesForPatient =
    data?.specialityById?.workgroups?.flatMap(wg => (wg?.messageForPatient && wg?.id === workgroupId ? [wg.messageForPatient] : [])) || [];
  const medicalInstructions = data?.specialityById?.medicalInstructions;
  const specialityName = data?.specialityById?.label;
  const isDesktopAndSmaller = useIsDesktop();

  const hasAnyContent = !!description || !!eltyInstructions || !!medicalInstructions || messagesForPatient.length > 0;
  if (!hasAnyContent) return null;

  const descriptionContent = (
    <>
      <ClinicHeader
        distance={null}
        logoSrc={logoUrl}
        clinicLink={clinicLink}
        isDomicile={isDomicile}
        clinicName={clinicName}
        isLicensed={isLicensed}
        completeAddress={completeAddress}
      />
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-3">
          <h3 className={`${isDesktopAndSmaller ? 'text-base' : 'text-lg'} font-semibold text-primary-active`}>{specialityName}</h3>
          <PriceSection hasDynamicPrice={hasDynamicPrice} isLicensedClinic={isLicensed} price={price} hasAvailableSlots />
        </div>
        <p className="text-sm text-neutral-darker">{description}</p>
      </div>
      <WorkgroupInstructionCard
        workgroupName={clinicName}
        medicalInstructions={medicalInstructions}
        messagesForPatient={messagesForPatient}
      />
      <EltyInstructionCard eltyInstructions={eltyInstructions} />
    </>
  );

  const actions = (
    <>
      {isLicensed && (
        <ELButton size="large" fullWidth variant="filled" color="primary" label="Scegli orario" onClick={onChooseDateAndClose} fullRadius />
      )}
      <ELButton
        size="large"
        fullWidth
        variant="text"
        color="primary"
        label="Chiudi"
        onClick={() => {
          onClose();
        }}
        fullRadius
      />
    </>
  );

  return (
    <>
      {!isDesktopAndSmaller && (
        <Sheet isOpen={true} onClose={onClose}>
          <div className="flex flex-col gap-8 px-20 py-16">
            <h3 className="text-lg font-bold text-primary md:text-3xl md:font-bold">Descrizione</h3>
            {descriptionContent}
            <div className="flex gap-4">{actions} </div>
          </div>
        </Sheet>
      )}
      {isDesktopAndSmaller && (
        <Modal className="m-0 h-full max-h-full w-full max-w-full px-3 py-5" onClose={onClose} isModalOpen>
          <div className="flex h-full max-h-full w-full flex-col">
            <div className="flex w-full items-center justify-between pb-6 pt-2">
              <div />
              <h1 className="text-base font-semibold text-primary">Descrizione</h1>
              <ELButton
                variant="text"
                size="small"
                color="primary"
                label="Chiudi"
                leadingIcon={<XMarkIcon className="size-6" />}
                aria-label="Chiudi"
                onClick={onClose}
              />
            </div>
            <div className="flex-1 overflow-y-auto">
              <div className="flex w-full flex-col items-stretch justify-start gap-6">{descriptionContent}</div>
            </div>
            <div className="flex w-full flex-col items-center justify-end gap-5 py-5">{actions}</div>
          </div>
        </Modal>
      )}
    </>
  );
};
